import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';
import {
  ArrowRight,
  MessageSquare,
  Camera,
  FileText,
  CheckCircle2,
  AlertCircle,
  ClipboardCheck,
  Loader2,
  Bell,
  type LucideIcon,
} from 'lucide-react';

export interface BookingCaseTimelineEvent {
  kind: 'booking_event';
  id: string;
  bookingId: string;
  eventType: string;
  actorId?: string | null;
  actorRole?: string | null;
  publicNote?: string | null;
  internalNote?: string | null;
  metadata?: Record<string, unknown> | null;
  isPublic?: boolean;
  previousStatus?: string | null;
  newStatus?: string | null;
  createdAt: number;
}

export interface JobCaseTimelineEvent {
  kind: 'job_event';
  id: string;
  jobId: string;
  eventType: string;
  previousState?: string | null;
  newState?: string | null;
  actorId?: string | null;
  actorRole?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: number;
}

export type CaseTimelineEvent = BookingCaseTimelineEvent | JobCaseTimelineEvent;

interface EventConfig {
  label: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

const EVENT_CONFIG: Record<string, EventConfig> = {
  STATUS_CHANGE: {
    label: 'Cambio de estado',
    icon: ArrowRight,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 border-blue-200',
  },
  STATE_CHANGE: {
    label: 'Cambio de estado',
    icon: ArrowRight,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100 border-blue-200',
  },
  TRANSITION_ATTEMPTED: {
    label: 'Intento de transición',
    icon: Loader2,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100 border-indigo-200',
  },
  REQUIREMENT_VALIDATION_FAILED: {
    label: 'Validación fallida',
    icon: AlertCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100 border-amber-200',
  },
  NOTE: {
    label: 'Nota',
    icon: MessageSquare,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100 border-gray-200',
  },
  PHOTO: {
    label: 'Foto',
    icon: Camera,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 border-purple-200',
  },
  ESTIMATE: {
    label: 'Presupuesto',
    icon: FileText,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100 border-emerald-200',
  },
  APPROVAL: {
    label: 'Aprobación',
    icon: CheckCircle2,
    color: 'text-green-600',
    bgColor: 'bg-green-100 border-green-200',
  },
  ASSIGNMENT: {
    label: 'Asignación',
    icon: ClipboardCheck,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100 border-slate-200',
  },
  NOTIFICATION: {
    label: 'Notificación',
    icon: Bell,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100 border-cyan-200',
  },
  CHECKLIST: {
    label: 'Checklist',
    icon: ClipboardCheck,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100 border-emerald-200',
  },
};

interface CaseTimelineProps {
  events?: CaseTimelineEvent[];
  loading?: boolean;
}

function formatStatusLabel(status?: string | null) {
  if (!status) return 'Desconocido';
  return status
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function formatRelativeTime(timestamp: number) {
  if (!timestamp) return 'Hace mucho';
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.round(diffMs / 60000);

  if (diffMinutes < 1) return 'Ahora';
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `Hace ${diffHours} h`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
  });
}

function formatFullDate(timestamp: number) {
  const date = new Date(timestamp * 1000);
  return date.toLocaleString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getEventConfig(eventType: string): EventConfig {
  return EVENT_CONFIG[eventType] ?? {
    label: eventType,
    icon: ArrowRight,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100 border-gray-200',
  };
}

function getActorLabel(role?: string | null, id?: string | null) {
  const actor = role ? role : 'sistema';
  if (actor === 'staff' && id) {
    return `Personal (${id.slice(0, 5)})`;
  }
  if (actor === 'customer') return 'Cliente';
  return actor.charAt(0).toUpperCase() + actor.slice(1);
}

function getEventDescription(event: CaseTimelineEvent) {
  const note =
    event.kind === 'booking_event'
      ? event.publicNote || event.internalNote
      : event.notes;
  const metadataMessage = event.metadata?.message || event.metadata?.description;

  if (event.kind === 'booking_event') {
    if (event.eventType === 'STATUS_CHANGE') {
      const prev = formatStatusLabel(event.previousStatus);
      const next = formatStatusLabel(event.newStatus);
      return `Estado: ${prev} → ${next}`;
    }
    if (note) return note;
  } else {
    if (event.eventType === 'STATE_CHANGE') {
      const prev = formatStatusLabel(event.previousState);
      const next = formatStatusLabel(event.newState);
      return `Estado: ${prev} → ${next}`;
    }
    if (note) return note;
  }

  if (metadataMessage) return metadataMessage as string;
  return 'Sin detalles disponibles';
}

export function CaseTimeline({ events = [], loading = false }: CaseTimelineProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">Línea de tiempo</p>
              <p className="text-xs text-muted-foreground">Cargando eventos...</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-3">
            {[1, 2, 3].map((idx) => (
              <div key={idx} className="flex gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!events.length) {
    return (
      <Card>
        <CardHeader>
          <p className="text-sm font-semibold">Línea de tiempo</p>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-muted-foreground">
            Todavía no se han registrado eventos para este caso.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Línea de tiempo</p>
          <p className="text-xs text-muted-foreground">Eventos recientes del caso</p>
        </div>
        <Badge variant="outline">{events.length} eventos</Badge>
      </CardHeader>
      <CardContent className="space-y-4 pt-2">
        {events.map((event) => {
          const config = getEventConfig(event.eventType);
          const Icon = config.icon;
          return (
            <div key={event.id} className="flex gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full border',
                  config.bgColor
                )}
              >
                <Icon className={cn('h-5 w-5', config.color)} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium capitalize">{config.label}</p>
                  <span
                    className="text-[11px] text-muted-foreground"
                    title={formatFullDate(event.createdAt)}
                  >
                    {formatRelativeTime(event.createdAt)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {getEventDescription(event)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Actor: {getActorLabel(event.actorRole, event.actorId)}
                </p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
